////// Lib. //////
//////////////////

import Component from "gia/Component";
import { gsap } from "gsap";
import { Draggable } from "gsap/Draggable";
import Splide from "@splidejs/splide";

gsap.registerPlugin(Draggable);

///// Assets /////
//////////////////

const GROUP_COLUMNS = 2;
const GROUP_TARGET_HEIGHT_VH = 0.925; // grouped block's total height, as a share of the viewport height
const MOBILE_MEDIA_QUERY = '(max-width: 767px), (max-height: 767px) and (pointer: coarse)';

// Comp. class ///
//////////////////

export default class StaffImages extends Component {
  /////////// Constructor ////////////
  ////////////////////////////////////

  constructor(el, options) {
    super(el);

    //// DOM references ////
    ////////////////////////

    this.ref = {
      item: [],
      slider: null,
    };

    /////// Options ////////
    ////////////////////////

    this.options = {
      name: "StaffImages",
      draggable: false,
      groupoverlap: 0.06, // share of an image's width that neighbours overlap by
      groupjitter: 0.04, // share of an image's width each tile may drift off the grid by
      ...options,
    };

    /////// State //////////
    ////////////////////////

    this._jitters = [];
    this._draggables = [];
    this._onResize = null;
    this._resizeTimer = null;
    this._names = [];
    this._focusListeners = [];
    this._isMobile = false;
    this._splide = null;
  }

  ///////////// Mount ////////////////
  ////////////////////////////////////

  mount() {
    if (!this.ref.item.length) return;

    this._isMobile = window.matchMedia(MOBILE_MEDIA_QUERY).matches;
    if (this._isMobile) {
      this._splide = new Splide(this.ref.slider, {
        type: 'slide',
        rewind: true,
        perPage: 1,
        arrows: false,
        pagination: true,
        gap: 'var(--unit-spacing)',
        drag: true,
        speed: 400,
        reducedMotion: {
          speed: 0,
          rewindSpeed: 0,
        },
      });
      this._splide.mount();
      this.element.dataset.placed = 'true';
      return;
    }

    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this._applyGroupSize();
        this._apply();
      }, 150);
    };

    window.addEventListener("resize", this._onResize);

    this._initFocusLink();

    // Aspect ratios come from the markup, so the boxes are measurable right away —
    // placement never has to wait for the images to load.
    requestAnimationFrame(() => {
      this._applyGroupSize();
      this._randomize();
      this._apply();
      this._initDrag();
      this.element.dataset.placed = "true";
    });
  }

  //////////// Unmount ///////////////
  ////////////////////////////////////

  unmount() {
    this._splide?.destroy(true);
    this._splide = null;

    clearTimeout(this._resizeTimer);
    window.removeEventListener("resize", this._onResize);
    this._onResize = null;

    this._draggables.forEach((draggable) => draggable.kill());
    this._draggables = [];

    this._focusListeners.forEach(({ el, type, handler }) => el.removeEventListener(type, handler));
    this._focusListeners = [];
    this._names = [];
  }

  /////////// Private ////////////////
  ////////////////////////////////////

  _apply() {
    const positions = this._groupedPositions();

    this.ref.item.forEach((item, i) => {
      const position = positions[i];
      if (!position) return;

      gsap.set(item, { x: position.x, y: position.y });
    });
  }

  // Re-rolls the per-image jitter; a resize keeps the same jitter and just re-flows it.
  _randomize() {
    this._jitters = this.ref.item.map(() => ({ x: Math.random() * 2 - 1, y: Math.random() * 2 - 1 }));
  }

  // ── Grouped layout ─────────────────────────────────────────────────────────

  // Solves for the shared item width that makes the block's total height equal
  // GROUP_TARGET_HEIGHT_VH, using only the images' intrinsic aspect ratios — row
  // heights and overlap both scale linearly with width, so no iteration is needed.
  _applyGroupSize() {
    if (!this.ref.item.length) return;

    const rowMaxInverseRatio = [];
    this.ref.item.forEach((item, i) => {
      const row = Math.floor(i / GROUP_COLUMNS);
      const ratio = this._aspectRatioOf(item);
      rowMaxInverseRatio[row] = Math.max(rowMaxInverseRatio[row] || 0, 1 / ratio);
    });

    const rows = rowMaxInverseRatio.length;
    const heightPerUnitWidth = rowMaxInverseRatio.reduce((sum, v) => sum + v, 0) - (rows - 1) * this.options.groupoverlap;
    if (heightPerUnitWidth <= 0) return;

    const targetHeight = window.innerHeight * GROUP_TARGET_HEIGHT_VH;
    this.element.style.setProperty("--staff-img-w", `${targetHeight / heightPerUnitWidth}px`);
  }

  _aspectRatioOf(item) {
    const image = item.querySelector("img");
    const [w, h] = (image ? getComputedStyle(image).getPropertyValue("--ar") : "").split("/").map(Number);
    return w && h ? w / h : 1;
  }

  // Captions are absolutely positioned, so an item's box is just its image —
  // tiles overlap edge to edge without captions skewing the spacing.
  _groupedPositions() {
    const bounds = this._bounds();
    const boxes = this.ref.item.map((item) => this._boxOf(item));
    const columns = Math.min(GROUP_COLUMNS, boxes.length);
    const overlap = boxes[0].width * this.options.groupoverlap;
    const jitter = boxes[0].width * this.options.groupjitter;

    const rowHeights = [];
    boxes.forEach((box, i) => {
      const row = Math.floor(i / GROUP_COLUMNS);
      rowHeights[row] = Math.max(rowHeights[row] || 0, box.height);
    });

    const rowTops = [];
    let offset = 0;
    rowHeights.forEach((height, row) => {
      rowTops[row] = offset;
      offset += height - overlap;
    });

    const lastRow = rowHeights.length - 1;
    const groupWidth = columns * boxes[0].width - (columns - 1) * overlap;
    const groupHeight = rowHeights.reduce((sum, height) => sum + height, 0) - lastRow * overlap;

    // The free area is shrunk by the jitter range so it can't push the block past the
    // container edges. The block itself is always flush against the right edge and
    // vertically centered — only the per-image jitter varies between loads.
    const rightMargin = 30; // px gap kept between the group and the right edge
    const startX = Math.max(0, bounds.width - groupWidth - jitter - rightMargin);
    const startY = Math.max(0, (bounds.height - groupHeight) / 2);

    return boxes.map((box, i) => {
      const row = Math.floor(i / GROUP_COLUMNS);
      const shift = this._jitters[i] || { x: 0, y: 0 };

      return {
        x: startX + (i % GROUP_COLUMNS) * (box.width - overlap) + shift.x * jitter,
        y: startY + rowTops[row] + shift.y * jitter,
      };
    });
  }

  _boxOf(item) {
    return { width: item.offsetWidth, height: item.offsetHeight };
  }

  // ── Focus link ─────────────────────────────────────────────────────────────

  // The name list lives outside this component's element (bottom left of the studio
  // layout), so it is matched up by index rather than by a g-ref.
  _initFocusLink() {
    const list = document.querySelector("[data-staff-names]");
    this._names = list ? Array.from(list.querySelectorAll("[data-staff-index]")) : [];

    [...this.ref.item, ...this._names].forEach((el) => {
      const index = Number(el.dataset.staffIndex);
      this._bindFocus(el, "pointerenter", () => this._setFocus(index));
      this._bindFocus(el, "pointerleave", () => this._setFocus(null));
    });
  }

  _bindFocus(el, type, handler) {
    el.addEventListener(type, handler);
    this._focusListeners.push({ el, type, handler });
  }

  _setFocus(index) {
    const targetIndex = Number.isInteger(index) ? index : null;

    this.ref.item.forEach((item) => {
      const itemIndex = Number(item.dataset.staffIndex);
      const isActive = targetIndex !== null && itemIndex === targetIndex;
      item.dataset.active = String(isActive);
      item.dataset.dimmed = String(targetIndex !== null && !isActive);
    });

    this._names.forEach((name) => {
      const nameIndex = Number(name.dataset.staffIndex);
      const isActive = targetIndex !== null && nameIndex === targetIndex;
      name.dataset.active = String(isActive);
      name.dataset.dimmed = String(targetIndex !== null && !isActive);
    });
  }

  // ── Drag ───────────────────────────────────────────────────────────────────

  _initDrag() {
    if (!this.options.draggable) return;

    this._draggables = Draggable.create(this.ref.item, {
      type: "x,y",
      bounds: this.element,
      cursor: "grab",
      activeCursor: "grabbing",
    });
  }

  // ── Geometry ───────────────────────────────────────────────────────────────

  _bounds() {
    return { width: this.element.clientWidth, height: this.element.clientHeight };
  }
}
